// htag-glossary.ts
// Plain-English definitions for all HTAG metrics
// Used by InfoTooltip component throughout SuburbIntelligenceSection

export interface MetricDefinition {
  term: string
  definition: string
  interpretation?: string   // optional: what a good/bad value looks like
}

export const HTAG_GLOSSARY: Record<string, MetricDefinition> = {
  rcs_overall: {
    term: 'RCS Overall',
    definition: 'HTAG\'s Relative Composite Score — a proprietary ranking from 0–100 that combines capital growth potential, cashflow strength, and risk profile into a single suburb score.',
    interpretation: 'Above 60 is strong. Below 40 suggests limited investment appeal across all dimensions.'
  },
  rcs_capital_growth: {
    term: 'Capital Growth RCS™',
    definition: 'Scores the suburb\'s capital growth potential from 0–100, based on 80+ underlying metrics including price momentum, demand/supply dynamics, and demographic trends.',
    interpretation: 'Above 60 signals above-average price growth likelihood. Below 40 suggests limited near-term upside.'
  },
  rcs_cashflow: {
    term: 'Cashflow RCS™',
    definition: 'Scores the suburb\'s rental income potential from 0–100. Considers gross yield, vacancy rate, rent growth trends, and rental demand depth.',
    interpretation: 'Above 60 indicates strong cashflow characteristics. Below 40 means the suburb likely runs negatively geared.'
  },
  rcs_lower_risk: {
    term: 'Lower Risk RCS™',
    definition: 'Scores the suburb\'s risk profile from 0–100. Higher scores indicate lower investment risk, factoring in economic diversity, market stability, flood/fire exposure, and hold period data.',
    interpretation: 'Above 60 is low-risk. Below 40 suggests elevated market or environmental risk.'
  },
  hapi: {
    term: 'HAPI Score',
    definition: 'Higher than Average Performance Index — HTAG\'s composite score (0–10) assessing overall suburb investment quality across all dimensions.',
  },
  volatility: {
    term: 'Volatility Index',
    definition: 'Measures price stability from 0–10. Lower scores mean more stable, predictable price movements. Higher scores indicate boom-bust risk.',
    interpretation: 'Below 4 is stable. Above 7 suggests significant price swings are common in this suburb.'
  },
  growth_rate_cycle: {
    term: 'Growth Rate Cycle',
    definition: 'The current phase of the suburb\'s property price cycle. Phases are: Bottom (prices bottomed out, recovery likely), Rising (prices accelerating upward), Peak (prices near top, growth slowing), Falling (prices declining). The (+) or (-) prefix indicates whether momentum is strengthening or weakening within that phase.',
    interpretation: 'Bottom/Rising are generally good entry points. Peak warrants caution. Falling suggests wait for the bottom.'
  },
  growth_low: {
    term: 'Projected Growth (Low)',
    definition: 'HTAG\'s conservative 2-year annual capital growth forecast based on current cycle position, demand/supply balance, and macroeconomic conditions.',
  },
  growth_high: {
    term: 'Projected Growth (High)',
    definition: 'HTAG\'s optimistic 2-year annual capital growth forecast. The wide range between low and high reflects genuine market uncertainty.',
  },
  cagr_price: {
    term: 'Price CAGR',
    definition: 'Compound Annual Growth Rate for property prices over the selected period. This is the annualised return, accounting for compounding — more meaningful than simple percentage change.',
    interpretation: 'Long-run Australian average is ~6–7%. Above 8% consistently is strong outperformance.'
  },
  cagr_rent: {
    term: 'Rent CAGR',
    definition: 'Compound Annual Growth Rate for median rents over the selected period. Shows how fast rental income has grown for landlords in this area.',
  },
  dom: {
    term: 'Days on Market',
    definition: 'The median number of days properties sit on the market before selling. A key indicator of buyer demand strength.',
    interpretation: 'Under 30 days = very high demand. 30–60 days = balanced. Over 60 days = soft demand or overpriced listings.'
  },
  clearance_rate: {
    term: 'Clearance Rate',
    definition: 'The percentage of properties sold at auction versus those passed in or withdrawn. Measured over rolling auctions in the area.',
    interpretation: 'Above 70% = very strong demand. 55–70% = healthy. Below 55% = buyer\'s market conditions.'
  },
  vacancy_rate: {
    term: 'Vacancy Rate',
    definition: 'The percentage of rental properties currently sitting empty and available to rent. Low vacancy = strong rental demand, landlord pricing power.',
    interpretation: 'Below 2% = extremely tight, strong rent growth likely. 2–3% = balanced. Above 3.5% = softer rental market, harder to find tenants.'
  },
  discounting: {
    term: 'Vendor Discounting',
    definition: 'The average percentage gap between a property\'s initial listing price and its final sale price. Lower discounting means sellers have more power.',
    interpretation: 'Below 2% = sellers in control. 2–4% = normal. Above 5% = buyers negotiating hard.'
  },
  buy_search_interest: {
    term: 'Buy Search Interest',
    definition: 'HTAG\'s index (0–10) measuring the volume of buyer search activity in this suburb relative to the national average. Based on listing portal search data.',
    interpretation: 'Above 7 = high buyer interest. Below 4 = limited search activity.'
  },
  rent_search_interest: {
    term: 'Rent Search Interest',
    definition: 'HTAG\'s index (0–10) measuring renter search demand relative to national average. High scores signal strong rental demand and low future vacancy risk.',
  },
  stock_on_market: {
    term: 'Stock on Market',
    definition: 'The percentage of total dwellings in the area currently listed for sale. Low stock = supply-constrained market, typically supports price growth.',
    interpretation: 'Below 1% = very low supply. 1–2% = normal. Above 3% = high supply, price pressure on sellers.'
  },
  inventory: {
    term: 'Inventory (Months of Supply)',
    definition: 'How many months it would take to sell all current listings at the current rate of sales. A key supply/demand balance indicator.',
    interpretation: 'Under 2 months = seller\'s market. 2–4 months = balanced. Over 4 months = buyer\'s market.'
  },
  hold_period: {
    term: 'Average Hold Period',
    definition: 'The average number of years property owners hold their investment before selling. Longer hold periods indicate confident ownership and less resale supply.',
    interpretation: 'Above 8 years = tightly held suburb, limited supply. Below 5 years = higher turnover, more resale supply.'
  },
  building_approvals: {
    term: 'Building Approvals',
    definition: 'The number of new dwelling construction approvals issued in the LGA over the past 12 months. High approvals signal upcoming supply increase.',
  },
  ba_ratio: {
    term: 'Building Approvals Ratio',
    definition: 'Building approvals as a percentage of total dwellings. Measures the pace of new supply relative to existing stock.',
    interpretation: 'Below 2% = modest new supply. Above 5% = significant pipeline, may pressure prices or rents.'
  },
  irsad: {
    term: 'IRSAD Decile',
    definition: 'Index of Relative Socio-economic Advantage and Disadvantage — an ABS measure of the social and economic conditions of an area. Scored 1–10 (10 = most advantaged).',
    interpretation: 'Above 7 = upper-middle to high socioeconomic area. Below 4 = areas of relative disadvantage.'
  },
  renter_ratio: {
    term: 'Renter-to-Owner Ratio',
    definition: 'The percentage of properties that are rented versus owner-occupied. High renter ratios can indicate investment-heavy suburbs with higher vacancy sensitivity.',
    interpretation: 'Below 30% renters = mainly owner-occupier, more price stable. Above 50% renters = investor-heavy, higher vacancy risk.'
  },
  years_to_own: {
    term: 'Years to Own',
    definition: 'An affordability metric — the estimated number of years for a median household income earner to save a deposit and pay off a median-priced property. High values = poor affordability.',
    interpretation: 'Under 20 years = affordable. 20–35 years = moderate. Over 40 years = severe affordability constraint, limits buyer pool.'
  },
  gpo_dist: {
    term: 'Distance to GPO',
    definition: 'Distance in kilometres to the nearest General Post Office, typically located in the CBD of the state capital. Used as a proxy for distance to the city centre.',
  },
  flood_percentile: {
    term: 'Flood Risk Percentile',
    definition: 'Where this LGA sits relative to all Australian LGAs for flood risk. Higher percentile = LOWER flood risk (94th percentile means only 6% of LGAs have lower flood risk).',
    interpretation: 'Above 80th percentile = low flood risk. Below 40th percentile = elevated flood risk — check individual property overlays.'
  },
  fire_percentile: {
    term: 'Bushfire Risk Percentile',
    definition: 'Where this LGA sits relative to all Australian LGAs for bushfire risk. Higher percentile = LOWER fire risk.',
    interpretation: 'Above 80th percentile = low bushfire risk. Below 50th percentile = meaningful fire exposure.'
  },
  edi: {
    term: 'Economic Diversity Index (EDI)',
    definition: 'Measures how diverse the local employment base is across industry sectors. Higher diversity = less dependence on a single industry, reducing economic shock risk.',
    interpretation: 'Above 70 = well-diversified economy. Below 40 = concentrated industry exposure (e.g. mining towns).'
  },
  madi: {
    term: 'Market Activity Index (MADI)',
    definition: 'HTAG\'s composite measure of overall property market activity — combining transaction volumes, listing activity, and search interest. Higher = more active market.',
    interpretation: 'Above 80 = highly active market with good liquidity. Below 40 = thin market, harder to buy/sell quickly.'
  },
  avm_estimate: {
    term: 'AVM Estimate',
    definition: 'Automated Valuation Model estimate — a data-driven property value estimate based on recent comparable sales, property attributes, and market conditions. Not a formal valuation.',
    interpretation: 'Treat as a directional guide ±10%. For finance decisions, always obtain a formal bank valuation.'
  },
  avm_range: {
    term: 'AVM Confidence Range',
    definition: 'The low-to-high range within which HTAG estimates the property value falls. A narrower range indicates higher confidence in the estimate.',
  },
}
